import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Box,
  Divider,
  TextField,
  MenuItem,
  Typography,
  Button,
  Fab,
  useMediaQuery,
  useTheme,
  TablePagination,
  Collapse,
  Popover,
  Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import DataTable from '../components/common/DataTable';
import TableFilters from '../components/common/TableFilters';
import ActionPopover from '../components/common/ActionPopover';
import ConfirmDialog from '../components/common/ConfirmDialog';
import AddEnquiryDialog from '../components/enquiry/AddEnquiryDialog';
import EnquiryDetailsPopover from '../components/enquiry/EnquiryDetailsPopover';
import EnquiryCardMobile from '../components/enquiry/EnquiryCardMobile';
import StatusChip from '../components/common/StatusChip';
// import AssignUserSelect from '../components/common/AssignUserSelect';
import DiscussionTextarea from '../components/common/DiscussionTextarea';
import { ENQUIRIES_COLUMNS } from '../config/tableColumnsConfig';
import { ENQUIRIES_FILTERS } from '../config/filterConfig';
import { ENQUIRY_STATUS } from '../config/statusConfig';
import enquiriesApi from '../services/enquiriesApi';
import { clearClientsListCache } from '../services/clientApi';
import { useToast } from '../components/common/ToastProvider';
import { useAuth } from '../contexts/AuthContext';
import { getDisplayStatus } from '../utils/enquiryDisplayUtils';
import { formatDate, formatDateTime, validateFollowUpDate } from '../utils/dateUtils';

/** Table sort is not wired in UI yet; keep default for API.list compatibility */
const DEFAULT_ENQUIRY_SORT = Object.freeze({ column: '', direction: '' });

/**
 * My Enquiry Page
 * Displays enquiries table with filters, sorting, and actions
 */
const MyEnquiry = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Mobile-only layout: cards + sticky header (max-width < 768px). Tablet & desktop keep table.
  const isMobileView = useMediaQuery('(max-width:767px)');
  const { isAdmin, user } = useAuth();
  // Filters: hidden by default on all screen sizes; toggle shows/hides with animation
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  // const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // Action popover state
  const [actionAnchor, setActionAnchor] = useState(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('');
  const [assignee, setAssignee] = useState('');
  const [discussion, setDiscussion] = useState('');
  const [nextFollowUpDate, setNextFollowUpDate] = useState('');
  // Update Enquiry popover validation errors (inline messages)
  const [updateFormErrors, setUpdateFormErrors] = useState({ status: '', nextFollowUpDate: '', discussion: '' });

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enquiryToDelete, setEnquiryToDelete] = useState(null);
  
  // Create/Edit dialog state – same dialog; enquiryIdForEdit set when editing
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [enquiryIdForEdit, setEnquiryIdForEdit] = useState(null);

  // Mark Quotation Sent state
  const [markQuotationLoading, setMarkQuotationLoading] = useState(false);

  // Enquiry details popover state
  const [detailsPopoverAnchor, setDetailsPopoverAnchor] = useState(null);
  const [selectedEnquiryDetails, setSelectedEnquiryDetails] = useState(null);

  // Enquiry history popover state
  const [historyPopoverAnchor, setHistoryPopoverAnchor] = useState(null);
  const [historyPopoverEnquiry, setHistoryPopoverEnquiry] = useState(null);

  // Employee filter options with counts (Admin only)
  const [employeeOptions, setEmployeeOptions] = useState([]);
  // City / state filter options from enquiry data
  const [cityOptions, setCityOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);

  const { showToast } = useToast();

  // Fetch enquiries (uses localStorage cache when the same filters/page/user was loaded before)
  const fetchEnquiries = useCallback(async () => {
    try {
      const response = await enquiriesApi.list(filters, DEFAULT_ENQUIRY_SORT, page, rowsPerPage, {
        cacheUserId: user?.employee_id || '',
      });
      setEnquiries(response.data);
      setTotalRows(response.total);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
    }
  }, [filters, page, rowsPerPage, user?.employee_id]);

  const cacheUserId = user?.employee_id || '';

  const loadEmployeeCounts = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const data = await enquiriesApi.getEmployeeEnquiryCounts({ cacheUserId });
      if (Array.isArray(data)) {
        const options = data.map(({ employee_id, employee_name, count }) => ({
          id: employee_id,
          name: `${employee_name} (${count})`,
        }));
        setEmployeeOptions(options);
      }
    } catch (error) {
      console.error('Error loading employee enquiry counts:', error);
    }
  }, [isAdmin, cacheUserId]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const { cities, states } = await enquiriesApi.getFilterOptions({ cacheUserId });
      setCityOptions(Array.isArray(cities) ? cities : []);
      setStateOptions(Array.isArray(states) ? states : []);
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  }, [cacheUserId]);

  const handleRefreshEnquiries = useCallback(() => {
    enquiriesApi.clearListCache();
    enquiriesApi.clearAuxiliaryCaches();
    clearClientsListCache();
    fetchEnquiries();
    loadEmployeeCounts();
    loadFilterOptions();
  }, [fetchEnquiries, loadEmployeeCounts, loadFilterOptions]);

  useEffect(() => {
    fetchEnquiries();
    // Note: React.StrictMode intentionally double-invokes effects in development
    // This helps catch bugs but may cause duplicate API calls in development mode
  }, [fetchEnquiries]);

  useEffect(() => {
    loadEmployeeCounts();
  }, [loadEmployeeCounts]);

  useEffect(() => {
    loadFilterOptions();
  }, [loadFilterOptions]);

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(0);
  };

  // Handle sort change
  // const handleSortChange = (e) => {
  //   const value = e.target.value;
  //   if (value === '') {
  //     setSort({ column: '', direction: '' });
  //   } else {
  //     const [column, direction] = value.split('-');
  //     setSort({ column, direction });
  //   }
  //   setPage(0);
  // };

  // Handle action button click
  const handleActionClick = (event, row) => {
    setActionAnchor(event.currentTarget);
    setSelectedEnquiry(row);
    setUpdateStatus(row.status);
    setAssignee('');
    setDiscussion('');
    const apiDate = row.next_follow_up_on || '';
    const normalizedDate = typeof apiDate === 'string' && apiDate.includes('T')
      ? apiDate.split('T')[0]
      : apiDate || '';
    setNextFollowUpDate(normalizedDate);
    setUpdateFormErrors({
      status: '',
      nextFollowUpDate: normalizedDate
        ? (validateFollowUpDate(normalizedDate) ? '' : 'Next follow-up date must be a future date.')
        : 'Next follow-up date is required.',
      discussion: '',
    });
  };

  // Update Enquiry form validation: all three fields mandatory; follow-up date must be future
  const isUpdateFormValid = useMemo(() => {
    const statusOk = Boolean(updateStatus?.trim());
    const dateOk = Boolean(nextFollowUpDate?.trim()) && validateFollowUpDate(nextFollowUpDate);
    const discussionOk = Boolean(discussion?.trim());
    return statusOk && dateOk && discussionOk;
  }, [updateStatus, nextFollowUpDate, discussion]);

  // Handle update enquiry (only when validation passes; no API call if invalid)
  const handleUpdateEnquiry = async () => {
    const statusErr = !updateStatus?.trim() ? 'Status is required.' : '';
    const dateErr = !nextFollowUpDate?.trim()
      ? 'Next follow-up date is required.'
      : !validateFollowUpDate(nextFollowUpDate)
        ? 'Next follow-up date must be a future date.'
        : '';
    const discussionErr = !discussion?.trim() ? 'Comment is required.' : '';
    setUpdateFormErrors({ status: statusErr, nextFollowUpDate: dateErr, discussion: discussionErr });
    if (statusErr || dateErr || discussionErr) return;

    if (selectedEnquiry) {
      try {
        const selectedStatus = updateStatus || selectedEnquiry.status || '';
        const previousUpdates = Array.isArray(selectedEnquiry.updates) ? selectedEnquiry.updates : [];
        const todayDate = new Date().toISOString().split('T')[0];

        const updatedUpdates = [
          ...previousUpdates,
          {
            date: todayDate,
            comment: discussion || '',
            discussion: discussion || '',
            new_status: selectedStatus,
            type: 'status_update',
          },
        ];

        const payload = {
          status: selectedStatus,
          next_follow_up_on: nextFollowUpDate || '',
          updates: updatedUpdates,
          ...(assignee ? { assigned_employee_current: assignee } : {}),
        };

        const response = await enquiriesApi.update(selectedEnquiry.id, payload);

        if (response.success) {
          showToast(response.message || 'Enquiry updated successfully', 'success');
          setActionAnchor(null);
          setSelectedEnquiry(null);
          setDiscussion('');
          setNextFollowUpDate('');
          setAssignee('');
          setUpdateFormErrors({ status: '', nextFollowUpDate: '', discussion: '' });
          enquiriesApi.clearListCache();
          fetchEnquiries();
        } else {
          showToast(response.error || 'Failed to update enquiry', 'error');
        }
      } catch (error) {
        console.error('Error updating enquiry:', error);
        showToast('Failed to update enquiry', 'error');
      }
    }
  };

  // Handle enquiry number click - show details popover
  const handleEnquiryNumberClick = (row, columnId, event) => {
    if (columnId === 'enquiryNumber') {
      setDetailsPopoverAnchor(event?.target || null);
      setSelectedEnquiryDetails(row);
    }
  };

  // Handle history icon click – open Enquiry History popover
  const handleHistoryClick = (event, row) => {
    setHistoryPopoverAnchor(event?.currentTarget ?? null);
    setHistoryPopoverEnquiry(row ?? null);
  };

  // Handle delete
  const handleDeleteClick = (row) => {
    setEnquiryToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (enquiryToDelete) {
      await enquiriesApi.delete(enquiryToDelete.id);
      setDeleteDialogOpen(false);
      setEnquiryToDelete(null);
      enquiriesApi.clearListCache();
      fetchEnquiries();
    }
  };

  // Build table columns with optional "Assigned To" column and enquiry display overrides (status + next follow up)
  const enquiriesColumns = useMemo(() => {
    const baseColumns = ENQUIRIES_COLUMNS.map((col) => ({ ...col }));

    // UI-only: display status override (Follow Up Missed when next_follow_up_on < today)
    const withStatusRender = baseColumns.map((col) => {
      if (col.id === 'status') {
        return {
          ...col,
          renderCell: (row) => {
            const { label } = getDisplayStatus(row);
            return <StatusChip status={label} />;
          },
        };
      }
      if (col.id === 'nextFollowUpOn') {
        return {
          ...col,
          renderCell: (row) => {
            const displayStatus = getDisplayStatus(row);
            const raw = row.next_follow_up_on ?? row.nextFollowUpOn;
            const hasDate = Boolean(raw != null && String(raw).trim() !== '');
            const text = hasDate ? formatDate(raw) : 'No Follow Up Date Added';
            return (
              <Typography
                variant="body2"
                component="span"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                  color: hasDate
                    ? (displayStatus.isMissed ? 'error.main' : 'text.primary')
                    : 'text.secondary',
                  fontStyle: hasDate ? 'normal' : 'italic',
                }}
              >
                {text}
              </Typography>
            );
          },
        };
      }
      return col;
    });

    if (!isAdmin) {
      return withStatusRender;
    }

    const hasAssignedTo = withStatusRender.some((col) => col.id === 'assignedTo');
    if (hasAssignedTo) {
      return withStatusRender;
    }

    const assignedToColumn = {
      id: 'assignedTo',
      label: 'Enq Owner',
      minWidth: 150,
    };

    const discussionsIndex = withStatusRender.findIndex((col) => col.id === 'discussions');
    if (discussionsIndex !== -1) {
      const updated = [...withStatusRender];
      updated.splice(discussionsIndex, 0, assignedToColumn);
      return updated;
    }

    return [...withStatusRender, assignedToColumn];
  }, [isAdmin]);

  // Handle create enquiry (CREATE mode – do not pass enquiryId)
  const handleCreateEnquiry = async (payload) => {
    try {
      const employeeId = user?.employee_id || '';
      const payloadWithEmployee = {
        ...payload,
        employee: employeeId,
      };

      const response = await enquiriesApi.create(payloadWithEmployee);
      if (response.success) {
        showToast(response.message || 'Enquiry created successfully', 'success');
        setCreateDialogOpen(false);
        setEnquiryIdForEdit(null);
        enquiriesApi.clearListCache();
        fetchEnquiries();
      } else {
        showToast(response.error || 'Failed to create enquiry', 'error');
      }
    } catch (error) {
      showToast('Failed to create enquiry', 'error');
    }
  };

  // Handle full enquiry update (EDIT mode – preserves existing _id, calls PUT)
  const handleUpdateEnquiryFull = async (payload, id) => {
    if (!id) return;
    try {
      const response = await enquiriesApi.update(id, payload);
      if (response.success) {
        showToast('Enquiry updated successfully', 'success');
        setCreateDialogOpen(false);
        setEnquiryIdForEdit(null);
        enquiriesApi.clearListCache();
        fetchEnquiries();
      } else {
        showToast(response.error || 'Failed to update enquiry', 'error');
      }
    } catch (error) {
      showToast('Failed to update enquiry', 'error');
    }
  };

  // Open dialog for edit – pre-fill with existing enquiry data
  const handleEditEnquiry = (row) => {
    if (!row?.id) return;
    setEnquiryIdForEdit(row.id);
    setCreateDialogOpen(true);
  };

  // Open dialog for create – ensure edit state is cleared
  const handleOpenCreateDialog = () => {
    setEnquiryIdForEdit(null);
    setCreateDialogOpen(true);
  };

  // User can edit if they can see the enquiry (same as create permission – authenticated user)
  const canEditEnquiry = true;

  // Handle mark quotation sent
  const handleMarkQuotationSent = async () => {
    if (!selectedEnquiry?.id) {
      showToast('No enquiry selected', 'error');
      return;
    }

    setMarkQuotationLoading(true);
    try {
      const response = await enquiriesApi.markQuotationSent(selectedEnquiry.id);
      if (response.success) {
        showToast('Quotation marked as sent successfully', 'success');
        setActionAnchor(null);
        setSelectedEnquiry(null);
        enquiriesApi.clearListCache();
        fetchEnquiries();
      } else {
        showToast(response.error || 'Failed to mark quotation sent', 'error');
      }
    } catch (error) {
      console.error('Error marking quotation sent:', error);
      showToast('Failed to mark quotation sent', 'error');
    } finally {
      setMarkQuotationLoading(false);
    }
  };

  // Sort dropdown options (shared between mobile and desktop)
  // const sortOptions = (
  //   <>
  //     <MenuItem value="">No Sort</MenuItem>
  //     <MenuItem value="enquiryNumber-asc">Enquiry Number (A-Z)</MenuItem>
  //     <MenuItem value="enquiryNumber-desc">Enquiry Number (Z-A)</MenuItem>
  //     <MenuItem value="lastUpdatedOn-asc">Last Updated (Oldest First)</MenuItem>
  //     <MenuItem value="lastUpdatedOn-desc">Last Updated (Newest First)</MenuItem>
  //     <MenuItem value="initialEnquiryDate-asc">Initial Date (Oldest First)</MenuItem>
  //     <MenuItem value="initialEnquiryDate-desc">Initial Date (Newest First)</MenuItem>
  //   </>
  // );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1, sm: 2 } }}>
      {/* ========== MOBILE ONLY (max-width < 768px): Card view + sticky header ========== */}
      {isMobileView && (
        <>
          {/* 1. Sticky header: Title + Add Enquiry */}
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: (t) => t.zIndex.appBar + 1,
              backgroundColor: 'background.paper',
              py: 1.5,
              pb: 1,
              mb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
                My Enquiry
              </Typography>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
                sx={{
                  backgroundColor: 'secondary.main',
                  color: 'secondary.contrastText',
                  fontSize: '0.8125rem',
                  '&:hover': { backgroundColor: 'secondary.dark' },
                }}
              >
                Add Enquiry
              </Button>
            </Box>
          </Box>

          {/* 2. Action row: Show Filters + Sort By */}
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              mb: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={handleRefreshEnquiries}
              sx={{ minWidth: 0, flex: 1, fontSize: '0.8125rem' }}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={() => setIsFiltersVisible((v) => !v)}
              sx={{ minWidth: 0, flex: 1, fontSize: '0.8125rem' }}
            >
              {isFiltersVisible ? 'Hide Filters' : 'Show Filters'}
            </Button>
            {/* <TextField
              select
              label="Sort By"
              value={DEFAULT_ENQUIRY_SORT.column && DEFAULT_ENQUIRY_SORT.direction ? `${DEFAULT_ENQUIRY_SORT.column}-${DEFAULT_ENQUIRY_SORT.direction}` : ''}
              onChange={handleSortChange}
              size="small"
              fullWidth
              sx={{ flex: 1, minWidth: 0, fontSize: '0.8125rem' }}
            >
              {sortOptions}
            </TextField> */}
          </Box>

          {/* 3. Collapsible filter section */}
          <Collapse
            in={isFiltersVisible}
            sx={{ overflow: 'hidden' }}
            timeout="auto"
          >
            <TableFilters
              expanded={isFiltersVisible}
              onExpandedChange={setIsFiltersVisible}
              filters={
                isAdmin
                  ? [...ENQUIRIES_FILTERS, { id: 'employee', label: 'Employee', type: 'employeeAutocomplete' }]
                  : ENQUIRIES_FILTERS
              }
              filterValues={filters}
              onFilterChange={handleFilterChange}
              statusOptions={ENQUIRY_STATUS}
              employeeOptions={isAdmin ? employeeOptions : []}
              cityOptions={cityOptions}
              stateOptions={stateOptions}
            />
          </Collapse>

          {/* 4. Enquiry cards list */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 2 }}>
            {enquiries.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                No enquiries found
              </Typography>
            ) : (
              enquiries.map((row, idx) => (
                <EnquiryCardMobile
                  key={row.id || row.enquiryNumber || idx}
                  enquiry={row}
                  onEnquiryNumberClick={handleEnquiryNumberClick}
                  onActionClick={handleActionClick}
                  onHistoryClick={handleHistoryClick}
                  showAssignedTo={isAdmin}
                />
              ))
            )}
          </Box>

          {/* 5. Pagination for mobile cards */}
          {totalRows > 0 && (
            <TablePagination
              component="div"
              count={totalRows}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                flexWrap: 'wrap',
                '& .MuiTablePagination-toolbar': { paddingLeft: 0, paddingRight: 0 },
                '& .MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem' },
              }}
            />
          )}
        </>
      )}

      {/* ========== TABLET & DESKTOP (unchanged): Table layout ========== */}
      {!isMobileView && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
            <Typography variant="h5" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              My Enquiry
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={handleRefreshEnquiries}
                sx={{ minWidth: 100, fontSize: '0.875rem' }}
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setIsFiltersVisible((v) => !v)}
                sx={{ minWidth: 120, fontSize: '0.875rem' }}
              >
                {isFiltersVisible ? 'Hide Filters' : 'Show Filters'}
              </Button>
              {/* <TextField
                select
                label="Sort By"
                value={DEFAULT_ENQUIRY_SORT.column && DEFAULT_ENQUIRY_SORT.direction ? `${DEFAULT_ENQUIRY_SORT.column}-${DEFAULT_ENQUIRY_SORT.direction}` : ''}
                onChange={handleSortChange}
                size="small"
                sx={{ minWidth: 180, fontSize: '0.875rem' }}
              >
                {sortOptions}
              </TextField> */}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenCreateDialog}
                sx={{
                  backgroundColor: 'secondary.main',
                  color: 'secondary.contrastText',
                  fontSize: '0.875rem',
                  '&:hover': { backgroundColor: 'secondary.dark' },
                }}
              >
                Add New Enquiry
              </Button>
            </Box>
          </Box>

          {/* Mobile FAB: only when tablet/desktop is small (sm) – keep for consistency */}
          {isMobile && (
            <Fab
              color="secondary"
              aria-label="add new enquiry"
              onClick={handleOpenCreateDialog}
              sx={{
                position: 'fixed',
                bottom: 16,
                right: 16,
                zIndex: (t) => t.zIndex.speedDial,
              }}
            >
              <AddIcon />
            </Fab>
          )}

          <Collapse in={isFiltersVisible} sx={{ overflow: 'hidden' }} timeout="auto">
            <TableFilters
              expanded={isFiltersVisible}
              onExpandedChange={setIsFiltersVisible}
              filters={
                isAdmin
                  ? [...ENQUIRIES_FILTERS, { id: 'employee', label: 'Employee', type: 'employeeAutocomplete' }]
                  : ENQUIRIES_FILTERS
              }
              filterValues={filters}
              onFilterChange={handleFilterChange}
              statusOptions={ENQUIRY_STATUS}
              employeeOptions={isAdmin ? employeeOptions : []}
              cityOptions={cityOptions}
              stateOptions={stateOptions}
            />
          </Collapse>

          <DataTable
            columns={enquiriesColumns}
            data={enquiries}
            onActionClick={handleActionClick}
            onDeleteClick={handleDeleteClick}
            onCellClick={handleEnquiryNumberClick}
            onEditClick={canEditEnquiry ? handleEditEnquiry : undefined}
            onHistoryClick={handleHistoryClick}
            canEdit={canEditEnquiry ? (() => true) : undefined}
            page={page}
            onPageChange={setPage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={setRowsPerPage}
            totalRows={totalRows}
            mobileHiddenColumns={['initialEnquiryDate', 'discussions', 'requirements']}
          />
        </>
      )}

      {/* Update Enquiry Popover */}
      <ActionPopover
        open={Boolean(actionAnchor)}
        anchorEl={actionAnchor}
        onClose={() => {
          setActionAnchor(null);
          setSelectedEnquiry(null);
          setUpdateFormErrors({ status: '', nextFollowUpDate: '', discussion: '' });
        }}
        title="Enquiry Actions"
        hideActions={true}
      >
        {/* Mark Quotation Sent Button (above status section) */}
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            fullWidth
            onClick={handleMarkQuotationSent}
            disabled={markQuotationLoading}
            sx={{
              backgroundColor: 'success.main',
              color: 'success.contrastText',
              '&:hover': { backgroundColor: 'success.dark' },
              '&:disabled': { backgroundColor: 'action.disabledBackground' },
            }}
          >
            {markQuotationLoading ? 'Processing...' : 'Mark Quotation Sent'}
          </Button>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Status Update Section */}
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Update Enquiry</Typography>
        <TextField
          select
          label="Enquiry Status"
          value={updateStatus}
          onChange={(e) => {
            setUpdateStatus(e.target.value);
            setUpdateFormErrors((prev) => ({ ...prev, status: '' }));
          }}
          error={Boolean(updateFormErrors.status)}
          helperText={updateFormErrors.status}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
        >
          {ENQUIRY_STATUS.map((status) => (
            <MenuItem key={status} value={status}>
              <StatusChip status={status} />
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Next Follow up Date"
          type="date"
          value={nextFollowUpDate}
          onChange={(e) => {
            const val = e.target.value;
            setNextFollowUpDate(val);
            const err = !val
              ? 'Next follow-up date is required.'
              : !validateFollowUpDate(val)
                ? 'Next follow-up date must be a future date.'
                : '';
            setUpdateFormErrors((prev) => ({ ...prev, nextFollowUpDate: err }));
          }}
          error={Boolean(updateFormErrors.nextFollowUpDate)}
          helperText={updateFormErrors.nextFollowUpDate}
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputLabelProps={{
            shrink: true,
          }}
        />
        <DiscussionTextarea
          value={discussion}
          onChange={(val) => {
            setDiscussion(val);
            setUpdateFormErrors((prev) => ({
              ...prev,
              discussion: val?.trim() ? '' : 'Comment is required.',
            }));
          }}
          error={Boolean(updateFormErrors.discussion)}
          helperText={updateFormErrors.discussion}
        />

        {/* Footer: Cancel and Update buttons */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', sm: 'row' }, justifyContent: 'flex-end', gap: { xs: 1, sm: 1.5 }, mt: 2.5, '& > *': { width: { xs: '100%', sm: 'auto' } } }}>
          <Button 
            onClick={() => {
              setActionAnchor(null);
              setSelectedEnquiry(null);
              setUpdateFormErrors({ status: '', nextFollowUpDate: '', discussion: '' });
            }} 
            variant="outlined"
            size="small"
            sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateEnquiry}
            variant="contained"
            color="primary"
            size="small"
            disabled={!isUpdateFormValid}
            sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
          >
            Update
          </Button>
        </Box>
      </ActionPopover>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Delete Enquiry"
        message={`Are you sure you want to delete enquiry ${enquiryToDelete?.enquiryNumber}?`}
      />

      {/* Create / Edit Enquiry Dialog – same dialog; enquiryIdForEdit triggers edit mode */}
      <AddEnquiryDialog
        open={createDialogOpen}
        onClose={() => {
          setCreateDialogOpen(false);
          setEnquiryIdForEdit(null);
        }}
        onSubmit={(payload, id) => (id ? handleUpdateEnquiryFull(payload, id) : handleCreateEnquiry(payload))}
        enquiryId={enquiryIdForEdit}
      />

      {/* Enquiry Details Popover */}
      <ActionPopover
        open={Boolean(detailsPopoverAnchor)}
        anchorEl={detailsPopoverAnchor}
        onClose={() => {
          setDetailsPopoverAnchor(null);
          setSelectedEnquiryDetails(null);
        }}
        hideActions={true}
      >
        <EnquiryDetailsPopover
          enquiry={selectedEnquiryDetails}
          onEditClick={
            canEditEnquiry
              ? (enquiry) => {
                  setDetailsPopoverAnchor(null);
                  setSelectedEnquiryDetails(null);
                  handleEditEnquiry(enquiry);
                }
              : undefined
          }
          canEdit={canEditEnquiry}
        />
      </ActionPopover>

      {/* Enquiry History Popover – read-only; closes on outside click; no history in View Enquiry popup */}
      <Popover
        open={Boolean(historyPopoverAnchor)}
        anchorEl={historyPopoverAnchor}
        onClose={() => {
          setHistoryPopoverAnchor(null);
          setHistoryPopoverEnquiry(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: { xs: 'calc(100vw - 32px)', sm: 360 },
              maxWidth: 420,
              maxHeight: '70vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 2,
              boxShadow: 4,
            },
          },
        }}
      >
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Enquiry History
          </Typography>
          {!historyPopoverEnquiry?.updates?.length ? (
            <Typography variant="body2" color="text.secondary">
              No updates available yet.
            </Typography>
          ) : (
            (() => {
              const updates = [...(historyPopoverEnquiry.updates || [])].sort((a, b) => {
                const da = a.date ? new Date(a.date).getTime() : 0;
                const db = b.date ? new Date(b.date).getTime() : 0;
                return da - db;
              });
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {updates.map((update, index) => (
                    <Box key={index}>
                      {index > 0 && <Divider sx={{ my: 1 }} />}
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          borderColor: 'divider',
                          bgcolor: 'action.hover',
                        }}
                      >
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            Status:
                          </Typography>
                          <StatusChip status={update.new_status || update.type || 'Update'} />
                        </Box>
                        {(update.comment || update.discussion) && (
                          <Typography variant="body2" sx={{ mb: 0.5, whiteSpace: 'pre-wrap' }}>
                            {update.comment || update.discussion}
                          </Typography>
                        )}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                          {(update.updated_by || update.employee_name) && (
                            <Typography variant="caption" color="text.secondary">
                              Updated by: {update.updated_by || update.employee_name}
                            </Typography>
                          )}
                          {(update.next_follow_up_on || update.next_follow_up) && (
                            <Typography variant="caption" color="text.secondary">
                              Next follow-up: {formatDate(update.next_follow_up_on || update.next_follow_up)}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {formatDateTime(update.date)}
                          </Typography>
                        </Box>
                      </Paper>
                    </Box>
                  ))}
                </Box>
              );
            })()
          )}
        </Box>
      </Popover>
    </Container>
  );
};

export default MyEnquiry;

